import { useSystemLogs, type SystemLogEntry } from "@/hooks/api/useSystemLogs";
import { useState, useEffect, useMemo, useRef } from "react";

const LEVEL_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  debug: { bg: "bg-gray-800",   text: "text-gray-400",  label: "DEBUG" },
  info:  { bg: "bg-blue-900",   text: "text-blue-300",  label: "INFO " },
  warn:  { bg: "bg-yellow-900", text: "text-yellow-300",label: "WARN " },
  error: { bg: "bg-red-900",    text: "text-red-300",   label: "ERROR" },
  fatal: { bg: "bg-purple-900", text: "text-purple-300",label: "FATAL" },
};

const SERVICE_COLORS: Record<string, string> = {
  backend:      "text-indigo-400",
  "llm-service":"text-emerald-400",
  frontend:     "text-orange-400",
};

const SERVICES = ["backend", "llm-service", "frontend"];
const LEVELS   = ["debug", "info", "warn", "error", "fatal"];
const PAGE_SIZE = 100;

function levelStyle(level: string) {
  return LEVEL_STYLES[level] ?? LEVEL_STYLES.info;
}

function serviceColor(service: string) {
  return SERVICE_COLORS[service] ?? "text-gray-400";
}

function toLocalDateKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(iso: string): string {
  const entryKey = toLocalDateKey(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (entryKey === toLocalDateKey(today.toISOString())) return "Today";
  if (entryKey === toLocalDateKey(yesterday.toISOString())) return "Yesterday";

  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2 sticky top-0 z-10 bg-gray-950/95 backdrop-blur-sm">
      <div className="flex-1 h-px bg-gray-800" />
      <span className="font-mono text-[11px] font-semibold text-gray-500 shrink-0 tracking-wider uppercase">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-800" />
    </div>
  );
}

function LogRow({ entry, expanded, onClick }: {
  entry: SystemLogEntry;
  expanded: boolean;
  onClick: () => void;
}) {
  const ls = levelStyle(entry.level);
  const hasData = !!entry.data && Object.keys(entry.data).length > 0;
  const time = new Date(entry.createdAt).toLocaleTimeString(undefined, {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });

  return (
    <div className={`border-b border-gray-800 px-3 py-1 select-text ${ls.bg} bg-opacity-20`}>
      <div
        className="flex items-start gap-2 font-mono text-xs cursor-pointer hover:opacity-80 transition-opacity"
        onClick={onClick}
      >
        <span className="shrink-0 text-gray-600 tabular-nums mt-0.5">{time}</span>
        <span className={`shrink-0 font-bold ${ls.text} mt-0.5`}>{ls.label}</span>
        <span className={`shrink-0 font-semibold ${serviceColor(entry.service)} mt-0.5`}>
          [{entry.service}]
        </span>
        <span className="text-gray-200 break-all">{entry.message}</span>
        {hasData && !expanded && (
          <span className="shrink-0 text-gray-600 text-[10px] mt-0.5">▶ data</span>
        )}
      </div>
      {expanded && hasData && (
        <pre className="mt-1 ml-[calc(3rem+6.5rem)] text-[11px] text-gray-400 whitespace-pre-wrap break-all border-l border-gray-700 pl-2">
          {JSON.stringify(entry.data, null, 2)}
        </pre>
      )}
    </div>
  );
}

type RenderedItem =
  | { type: "separator"; label: string; dateKey: string }
  | { type: "entry"; entry: SystemLogEntry };

function buildRenderedItems(entries: SystemLogEntry[]): RenderedItem[] {
  const items: RenderedItem[] = [];
  let lastDateKey: string | null = null;

  for (const entry of entries) {
    const dateKey = toLocalDateKey(entry.createdAt);
    if (dateKey !== lastDateKey) {
      items.push({ type: "separator", label: formatDateLabel(entry.createdAt), dateKey });
      lastDateKey = dateKey;
    }
    items.push({ type: "entry", entry });
  }
  return items;
}

export default function SystemLogs() {
  const [serviceFilter, setServiceFilter] = useState("");
  const [levelFilter,   setLevelFilter]   = useState("");
  const [expandedId,    setExpandedId]    = useState<string | null>(null);
  const [offset,        setOffset]        = useState(0);
  const [allEntries,    setAllEntries]    = useState<SystemLogEntry[]>([]);
  const lastAppliedOffset = useRef(-1);

  // Reset when filters change
  useEffect(() => {
    setOffset(0);
    setAllEntries([]);
    lastAppliedOffset.current = -1;
  }, [serviceFilter, levelFilter]);

  const { data, isLoading, isFetching, refetch } = useSystemLogs({
    limit:   PAGE_SIZE,
    offset,
    service: serviceFilter || undefined,
    level:   levelFilter   || undefined,
  });

  // Append new page; guard against double-fires from React Query's stale data
  useEffect(() => {
    if (!data?.data || lastAppliedOffset.current === offset) return;
    lastAppliedOffset.current = offset;
    if (offset === 0) {
      setAllEntries(data.data);
    } else {
      setAllEntries((prev) => [...prev, ...data.data]);
    }
  }, [data, offset]);

  const hasMore = data?.hasMore ?? false;

  function handleRefresh() {
    setOffset(0);
    setAllEntries([]);
    lastAppliedOffset.current = -1;
    void refetch();
  }

  const renderedItems = useMemo(() => buildRenderedItems(allEntries), [allEntries]);

  return (
    <div className="flex flex-col h-screen bg-gray-950 text-gray-200">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-gray-900 border-b border-gray-800 shrink-0 flex-wrap">
        <span className="font-mono font-bold text-sm text-gray-100 mr-2">System Logs</span>

        <div className="flex-1" />

        <select
          className="text-xs bg-gray-800 border border-gray-700 text-gray-300 rounded px-2 py-1 font-mono"
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
        >
          <option value="">all services</option>
          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select
          className="text-xs bg-gray-800 border border-gray-700 text-gray-300 rounded px-2 py-1 font-mono"
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
        >
          <option value="">all levels</option>
          {LEVELS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>

        <button
          className="text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 rounded px-2 py-1 font-mono transition-colors disabled:opacity-50"
          onClick={handleRefresh}
          disabled={isFetching}
        >
          ↻ refresh
        </button>
      </div>

      {/* Log area */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && allEntries.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-600 font-mono text-sm">
            loading…
          </div>
        ) : allEntries.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-700 font-mono text-sm">
            no entries
          </div>
        ) : (
          <>
            {renderedItems.map((item) =>
              item.type === "separator" ? (
                <DateSeparator key={`sep-${item.dateKey}`} label={item.label} />
              ) : (
                <LogRow
                  key={item.entry.id}
                  entry={item.entry}
                  expanded={expandedId === item.entry.id}
                  onClick={() =>
                    setExpandedId(expandedId === item.entry.id ? null : item.entry.id)
                  }
                />
              )
            )}

            {/* Load more / end indicator */}
            <div className="flex justify-center py-4">
              {hasMore ? (
                <button
                  className="text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 rounded px-4 py-2 font-mono transition-colors disabled:opacity-50"
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  disabled={isFetching}
                >
                  {isFetching ? "loading…" : `load older entries`}
                </button>
              ) : (
                <span className="font-mono text-[11px] text-gray-700">— end of logs —</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Status bar */}
      <div className="px-4 py-1 bg-gray-900 border-t border-gray-800 shrink-0">
        <span className="font-mono text-[11px] text-gray-600">
          {allEntries.length} entries shown{hasMore ? " · more available" : ""}
          {isFetching && " · loading…"}
        </span>
      </div>
    </div>
  );
}
